import { PlayCircleIcon } from '@heroicons/react/24/outline';
import type { ComponentType } from 'react';
import { ConfirmModalShell } from '../approval-review/ConfirmModalShell';
import { ConfirmModalHeader } from '../approval-review/ConfirmModalHeader';
import { ConfirmModalFooter } from '../approval-review/ConfirmModalFooter';
import type { VaPlainHandoffKind } from './useVaHandoff';

interface HandoffCopy {
    icon: ComponentType<{ className?: string }>;
    title: string;
    body: string;
    confirmLabel: string;
}

const COPY: Record<VaPlainHandoffKind, HandoffCopy> = {
    'continue-treatment': {
        icon: PlayCircleIcon,
        title: 'Continue treatment',
        body: 'No VA is open. The case moves back to Treatment in progress.',
        confirmLabel: 'Continue treatment'
    }
};

interface VaHandoffConfirmModalProps {
    kind: VaPlainHandoffKind;
    submitting: boolean;
    error: string | null;
    onConfirm: () => void;
    onClose: () => void;
}

export function VaHandoffConfirmModal({
    kind,
    submitting,
    error,
    onConfirm,
    onClose
}: VaHandoffConfirmModalProps) {
    const copy = COPY[kind];
    return (
        <ConfirmModalShell widthClassName="w-[440px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={copy.icon}
                iconClassName="w-5 h-5 text-gray-700"
                title={copy.title}
                onClose={onClose}
                disabled={submitting}
            />
            <div className="px-5 py-4 flex flex-col gap-3">
                <p className="text-sm text-gray-600">{copy.body}</p>
                {error && (
                    <p
                        role="alert"
                        className="text-sm text-red-800 bg-red-50 border border-red-300 rounded-md px-3 py-2"
                    >
                        {error}
                    </p>
                )}
            </div>
            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={onConfirm}
                cancelDisabled={submitting}
                confirmDisabled={submitting}
                confirmLabel={submitting ? 'Working…' : copy.confirmLabel}
                confirmColorClassName="bg-gray-800 text-white hover:bg-gray-900"
            />
        </ConfirmModalShell>
    );
}
